import { API_BASE_URL } from "./client";

export class PublicApiError extends Error {
  readonly status: number;
  readonly detail: string | null;

  constructor(status: number, detail?: string) {
    super(detail ?? `HTTP ${status}`);
    this.name = "PublicApiError";
    this.status = status;
    this.detail = detail ?? null;
  }
}

export type PublicFetchFailure =
  | { kind: "network"; error: unknown }
  | { kind: "http"; response: Response };

export interface PublicFetchRetryOptions {
  /** Total attempts, including the first request. */
  attempts?: number;
  /** Fixed delay or a delay calculated from the failed attempt number. */
  backoffMs?: number | ((failedAttempt: number) => number);
  /** Network failures qualify by default. HTTP failures require an explicit opt-in. */
  shouldRetry?: (failure: PublicFetchFailure) => boolean;
}

export interface PublicFetchOptions extends RequestInit {
  retry?: PublicFetchRetryOptions;
}

export async function publicFetch<T>(
  path: string,
  options: PublicFetchOptions = {},
  baseUrl = API_BASE_URL,
): Promise<T> {
  const { retry, ...init } = options;
  const attempts = retry?.attempts ?? 1;
  const shouldRetry =
    retry?.shouldRetry ??
    ((failure: PublicFetchFailure) => failure.kind === "network");
  const backoffMs = (failedAttempt: number) =>
    typeof retry?.backoffMs === "function"
      ? retry.backoffMs(failedAttempt)
      : (retry?.backoffMs ?? 250 * 2 ** (failedAttempt - 1));

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    let res: Response;
    try {
      res = await fetch(`${baseUrl}${path}`, init);
    } catch (error) {
      if (attempt === attempts || !shouldRetry({ kind: "network", error })) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, backoffMs(attempt)));
      continue;
    }

    // 4xx responses are definitive client outcomes. In particular, retrying 429
    // would consume another rate-limit slot and extend the lockout window.
    const retryableHttpStatus = res.status >= 500 && res.status < 600;
    if (
      !res.ok &&
      retryableHttpStatus &&
      attempt < attempts &&
      shouldRetry({ kind: "http", response: res })
    ) {
      await new Promise((resolve) => setTimeout(resolve, backoffMs(attempt)));
      continue;
    }

    if (!res.ok) {
      const contentType = res.headers.get("Content-Type") ?? "";
      const body = contentType.includes("application/json")
        ? ((await res.json()) as { detail?: string })
        : null;
      throw new PublicApiError(res.status, body?.detail);
    }
    return res.json();
  }

  throw new Error("publicFetch requires at least one attempt");
}
